/**
 * 検定の設定。比率のタブと平均のタブで同じものを使う。
 *
 * 同じ実験について「比率は 95% 両側、平均は 90% 片側」と条件がずれると、
 * どちらの数字を採用するかが恣意的になる。設定を 1 つにまとめて共有し、
 * タブを移っても条件が変わらないようにしている。
 */

import type { AnalysisSettings, SequentialContext } from '../lib/stats/analysis'
import { CORRECTION_LABELS, type CorrectionMethod } from '../lib/stats/multiple'
import { alphaFromConfidence } from '../lib/stats/proportion'
import { SPENDING_LABELS, type SpendingFunction } from '../lib/stats/sequential'
import type { Tail } from '../lib/stats/types'
import { formatAlpha, formatPercent } from '../lib/format'
import { MAX_LOOKS } from '../lib/urlState'
import { NumberField } from './NumberField'
import { SegmentedControl } from './SegmentedControl'

interface Props {
  settings: AnalysisSettings
  onSettingsChange: (settings: AnalysisSettings) => void
}

const CONFIDENCE_OPTIONS = [
  { value: '0.9', label: '90%' },
  { value: '0.95', label: '95%' },
  { value: '0.99', label: '99%' },
] as const

const TAIL_OPTIONS = [
  {
    value: 'two-sided' as Tail,
    label: '両側',
    title: '上がったか下がったかを問わず「差があるか」を見る。既定はこちら。',
  },
  {
    value: 'greater' as Tail,
    label: '片側 (増加)',
    title: '比較群が対照群より高いかだけを見る。下がる可能性を検定しない。',
  },
]

const CORRECTION_OPTIONS = (
  ['none', 'holm', 'bonferroni'] as CorrectionMethod[]
).map((value) => ({
  value,
  label: CORRECTION_LABELS[value],
  title:
    value === 'none'
      ? '補正しない。比較が 1 件だけならこれでよい。'
      : value === 'holm'
        ? 'Bonferroni より検出力を落とさずに全体の誤検出率を抑える。3 群以上ならこちら。'
        : '最も保守的。信頼区間も同時被覆になるよう広げる。',
}))

const SPENDING_OPTIONS = (
  ['obrien-fleming', 'pocock'] as SpendingFunction[]
).map((value) => ({
  value,
  label: SPENDING_LABELS[value],
  title:
    value === 'obrien-fleming'
      ? '序盤はほとんど水準を使わず、早期に止めるには強い証拠を求める。最後まで進んだときの水準が落ちにくい。'
      : '毎回ほぼ同じ水準を配分する。早期に止まりやすいが、最後まで進んだ場合の水準は厳しいまま。',
}))

/** ⓘ アイコンで出す説明。 */
export const SETTING_HINTS = {
  confidence:
    '信頼区間の幅と、有意と判定する厳しさを決めます。95% は「同じ実験を繰り返したとき、' +
    '信頼区間が真の値を含む割合が 95% になる幅」という意味です。上げるほど区間は広くなり、' +
    '差があるとは言いにくくなります。迷ったら 95% のままで問題ありません。',
  tail:
    '両側は「上がったか下がったかを問わず差があるか」、片側は「上がったかどうか」だけを見ます。' +
    '片側は同じデータでも有意になりやすい代わりに、悪化を検出できません。' +
    '結果を見てから片側に変えるのは検定として不正です。',
  correction:
    '比較の回数が増えるほど、本当は差がなくても偶然どれかが有意になります' +
    '（対照群に比較群 2 つを 5% 水準で当てると、2 回の比較で約 10%）。' +
    'Holm と Bonferroni はこの確率を全体で' +
    '有意水準まで抑えます。Holm のほうが検出力を落としません。比較が 1 件だけなら補正は不要です。',
  spending:
    "有意水準を確認のたびにどう配るかを決めます。O'Brien-Fleming 型は序盤にほとんど配分せず、" +
    '早く止めるには極端に強い証拠を求める代わりに、最後まで進んだときの水準をあまり削りません。' +
    'Pocock 型は毎回ほぼ同じ水準を配るので早期に止まりやすいですが、' +
    "最後まで進んだ場合の基準は厳しいままになります。迷ったら O'Brien-Fleming 型が無難です。",
} as const

/** 途中確認を有効にしたときの初期値。5 回は最もよくある運用（週次で 5 週）。 */
const DEFAULT_SEQUENTIAL = {
  looks: 5,
  currentLook: 1,
  spending: 'obrien-fleming' as SpendingFunction,
}

export function TestSettingsCard({ settings, onSettingsChange }: Props) {
  return (
    <section className="card">
      <h2 className="card-title">検定の設定</h2>
      <p className="card-hint">
        迷ったら既定のまま（95% / 両側）で問題ありません。比較群が 2 つ以上あるときは
        多重比較補正を有効にしてください。この設定は両方のタブで共通です。
      </p>
      <div className="settings-row">
        <SegmentedControl
          label="信頼水準"
          hint={SETTING_HINTS.confidence}
          value={String(settings.confidence)}
          options={CONFIDENCE_OPTIONS}
          onChange={(value) =>
            onSettingsChange({ ...settings, confidence: Number(value) })
          }
        />
        <SegmentedControl
          label="検定の向き"
          hint={SETTING_HINTS.tail}
          value={settings.tail}
          options={TAIL_OPTIONS}
          onChange={(tail) => onSettingsChange({ ...settings, tail })}
        />
        <SegmentedControl
          label="多重比較補正"
          hint={SETTING_HINTS.correction}
          value={settings.correction}
          options={CORRECTION_OPTIONS}
          onChange={(correction) => onSettingsChange({ ...settings, correction })}
        />
      </div>
    </section>
  )
}

interface SequentialProps extends Props {
  /** 分析できているときの逐次確認の文脈。棄却限界の表に使う。 */
  sequential: SequentialContext | null
}

export function SequentialCard({
  settings,
  onSettingsChange,
  sequential,
}: SequentialProps) {
  return (
    <section className="card">
      <h2 className="card-title">途中経過の確認</h2>
      <p className="card-hint">
        実験の途中で結果を見て、有意になった時点で止めるつもりなら、覗く回数をここで宣言してください。
        見た回数だけ棄却の機会が増えるため、補正せずに 5 回覗くと、本当は差がなくても
        どれかの時点で有意になる確率が約 14% まで膨らみます。宣言すると、各回に使える
        有意水準を配分し、何回覗いても全体で有意水準を超えないようにします。
      </p>

      <div className="groups-actions" style={{ marginTop: 0 }}>
        <button
          type="button"
          className="button"
          aria-pressed={settings.sequential !== null}
          onClick={() =>
            onSettingsChange({
              ...settings,
              sequential: settings.sequential ? null : DEFAULT_SEQUENTIAL,
            })
          }
        >
          途中で結果を見る
        </button>
      </div>

      {settings.sequential && (
        <>
          <div className="settings-row" style={{ marginTop: 16 }}>
            <NumberField
              label="確認する回数（予定）"
              ariaLabel="確認する回数（予定）"
              value={settings.sequential.looks}
              min={1}
              max={MAX_LOOKS}
              step={1}
              onChange={(looks) =>
                onSettingsChange({
                  ...settings,
                  sequential: { ...settings.sequential!, looks },
                })
              }
            />
            <NumberField
              label="今回が何回目か"
              ariaLabel="今回が何回目か"
              value={settings.sequential.currentLook}
              min={1}
              // 回数の欄を消している最中は NaN になる。属性に渡すと
              // React が警告を出すので、上限なしとして扱う
              max={
                Number.isFinite(settings.sequential.looks)
                  ? settings.sequential.looks
                  : undefined
              }
              step={1}
              onChange={(currentLook) =>
                onSettingsChange({
                  ...settings,
                  sequential: { ...settings.sequential!, currentLook },
                })
              }
            />
            <SegmentedControl
              label="アルファ消費関数"
              hint={SETTING_HINTS.spending}
              value={settings.sequential.spending}
              options={SPENDING_OPTIONS}
              onChange={(spending) =>
                onSettingsChange({
                  ...settings,
                  sequential: { ...settings.sequential!, spending },
                })
              }
            />
          </div>

          {sequential && (
            <BoundaryTable
              sequential={sequential}
              plainAlpha={alphaFromConfidence(settings.confidence)}
            />
          )}
        </>
      )}
    </section>
  )
}

/**
 * 予定している確認ごとの棄却限界。
 *
 * 「なぜ今回の基準がこの値なのか」は 1 回分の数字だけでは伝わらない。
 * 全回分を並べると、序盤に厳しく終盤に緩むという配分の形がそのまま見える。
 */
function BoundaryTable({
  sequential,
  plainAlpha,
}: {
  sequential: SequentialContext
  /** 途中確認を宣言しなかった場合の有意水準。比較のために添える。 */
  plainAlpha: number
}) {
  const { currentLook } = sequential.settings

  return (
    <div className="boundary-wrap">
      <table className="boundary-table">
        <caption className="visually-hidden">
          確認ごとの棄却限界と配分された有意水準
        </caption>
        <thead>
          <tr>
            <th scope="col">確認</th>
            <th scope="col">進捗</th>
            <th scope="col">必要な p 値</th>
            <th scope="col">棄却限界 z</th>
            <th scope="col">累積で使う水準</th>
          </tr>
        </thead>
        <tbody>
          {sequential.boundaries.map((boundary, index) => (
            <tr
              key={boundary.informationTime}
              aria-current={index + 1 === currentLook ? 'true' : undefined}
            >
              <th scope="row">
                {index + 1} 回目
                {index + 1 === currentLook && (
                  <span className="boundary-now">今回</span>
                )}
              </th>
              <td>{formatPercent(boundary.informationTime, 0)}</td>
              <td>&lt; {formatAlpha(boundary.nominalAlpha)}</td>
              <td>{boundary.z.toFixed(3)}</td>
              <td>{formatAlpha(boundary.cumulativeAlpha)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="card-hint" style={{ marginTop: 10 }}>
        進捗は予定サンプルサイズに対する割合で、確認は等間隔に行う前提で計算しています。
        最後まで進んだときの基準が{' '}
        {formatAlpha(sequential.boundaries.at(-1)!.nominalAlpha)} と、1 回だけ見る場合の{' '}
        {formatAlpha(plainAlpha)} より厳しいのは、
        途中で覗いた回数のぶんを差し引いているためです。
      </p>
    </div>
  )
}
